// The commissioner log, from the screen: the first page of `GET
// /leagues/:id/audit`, and the pages after it on asking. AUDIT_LOG.md, J5.
//
// A PRACTICE LEAGUE'S LOG IS EMPTY. The sentences are the server's
// (`packages/league/src/audit.ts`), and rebuilding them here from the
// simulator's state would be a second copy of a rule a second client would
// have to reinvent; the simulator's commissioner does nothing a log records.
//
// A PAGE AFTER THE FIRST THAT FAILS SAYS SO (`olderFailure`) and keeps what is
// shown, rather than leaving More looking as though it did nothing.

import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuditPage, type AuditEntry, type Snapshot } from '@fsp/domain';
import { get, messageOf } from './api';
import { useLeagueSource, useLeagueView } from './leagueSource';
import { leagueIdFromPath } from './route';

const EMPTY: AuditPage = { items: [], nextCursor: null };
const SIM_AS_OF = new Date(0).toISOString();

export function useAuditLog(): {
  snapshot: Snapshot<AuditEntry[]>;
  older: (() => void) | null;
  loadingOlder: boolean;
  olderFailure: string | null;
} {
  const { pathname } = useLocation();
  const leagueId = useMemo(() => leagueIdFromPath(pathname), [pathname]);
  const source = useLeagueSource();
  const path = leagueId === null ? null : `/leagues/${leagueId}/audit`;
  const first = useLeagueView(path, AuditPage, source === 'sim' ? EMPTY : null, SIM_AS_OF);

  // The pages after the first, read on asking; a new first page starts over.
  const [more, setMore] = useState<{
    key: unknown;
    items: AuditEntry[];
    cursor: string | null;
    failure: string | null;
  }>({ key: null, items: [], cursor: null, failure: null });
  const [loadingOlder, setLoadingOlder] = useState(false);
  // THE LAST GOOD FIRST PAGE, so a refresh that fails keeps the older pages
  // already read beside it, and More with them (the review of J5, W3).
  const firstValue =
    first.state === 'ready' ? first.value : first.state === 'failed' ? first.lastValue : null;
  const extra =
    more.key === firstValue ? more : { key: firstValue, items: [], cursor: null, failure: null };
  const cursor = extra.cursor ?? firstValue?.nextCursor ?? null;
  const older =
    path === null || firstValue === null || cursor === null || cursor === '' || loadingOlder
      ? null
      : () => {
          setLoadingOlder(true);
          void get(`${path}?cursor=${encodeURIComponent(cursor)}`, AuditPage).then((reply) => {
            setLoadingOlder(false);
            if (reply.status !== 'ok') {
              setMore({
                ...extra,
                failure: messageOf(reply, 'The older entries could not be read. Try again.'),
              });
              return;
            }
            setMore({
              key: firstValue,
              items: [...extra.items, ...reply.data.items],
              // '' marks "read to the end", so More goes away.
              cursor: reply.data.nextCursor ?? '',
              failure: null,
            });
          });
        };
  const snapshot = useMemo((): Snapshot<AuditEntry[]> => {
    if (first.state === 'ready') return { ...first, value: [...first.value.items, ...extra.items] };
    if (first.state === 'failed')
      return {
        ...first,
        lastValue: first.lastValue === null ? null : [...first.lastValue.items, ...extra.items],
      };
    return first;
  }, [first, extra.items]);
  return { snapshot, older, loadingOlder, olderFailure: extra.failure };
}
